import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Archive, Lock, Users } from "lucide-react";

import type { Channel } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Channel };

type ChannelCardProps = Channel & BaseEntityCardProps;

// Avatar component for ChannelCard
export function ChannelCardAvatar({
  is_im,
  im_user_image_url,
  im_user_name,
  className,
  style,
}: Partial<ChannelCardProps>) {
  return (
    <BaseEntityCardIcon
      className={cn("overflow-hidden", className)}
      style={style}
    >
      {is_im && im_user_image_url ? (
        <img
          src={im_user_image_url}
          alt={im_user_name || "User"}
          className="h-full w-full object-cover"
        />
      ) : (
        <Users className="h-5 w-5 text-muted-foreground" />
      )}
    </BaseEntityCardIcon>
  );
}

export function ChannelCard({
  platform,
  name,
  name_normalized,
  is_channel,
  is_im,
  is_mpim,
  is_private,
  is_archived,
  im_user_name,
  im_user_image_url,
  topic,
  purpose,
  num_members,
  unread_count,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: ChannelCardProps) {
  // Get channel type for display
  const getChannelType = () => {
    if (is_im) return "Direct Message";
    if (is_mpim) return "Group Message";
    if (is_channel) return is_private ? "Private Channel" : "Public Channel";
    return "Channel";
  };

  // Get the display name
  const displayName =
    is_im && im_user_name ? im_user_name : name_normalized || name;

  // Prepare description text (topic or purpose)
  const description = topic || purpose || "";
  const descriptionPreview =
    description && description.length > 100
      ? `${description.substring(0, 100)}...`
      : description;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <ChannelCardAvatar
        is_im={is_im}
        im_user_image_url={im_user_image_url}
        im_user_name={im_user_name}
      />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>
            {displayName}
            {is_archived && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Archive className="inline h-4 w-4 ml-1.5 text-muted-foreground align-middle" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Archived</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {is_private && !is_im && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Lock className="inline h-4 w-4 ml-1.5 text-muted-foreground align-middle" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Private</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <BaseEntityCardDescription className="cursor-default">
                    {descriptionPreview}
                  </BaseEntityCardDescription>
                </TooltipTrigger>
                {description && description.length > 100 && (
                  <TooltipContent className="max-w-xs break-words">
                    <p>{description}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}

          <BaseEntityCardTags>
            <Badge variant="outline" className="text-xs">
              {getChannelType()}
            </Badge>

            {num_members != null && num_members > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs">
                      <Users className="h-3 w-3 mr-1" />
                      {num_members}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {num_members} member{num_members !== 1 ? "s" : ""}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {unread_count != null && unread_count > 0 && (
              <Badge className="text-xs bg-red-100 text-red-800">
                {unread_count} unread
              </Badge>
            )}
          </BaseEntityCardTags>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function ChannelCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[90px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-1.5" />
        <Skeleton className="h-4 w-3/4 mb-1.5" />
        <Skeleton className="h-3 w-1/2" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
